import { getCustomTypesOptions } from "@prismicio/editor-fields"
import { Box, Skeleton, theme } from "@prismicio/editor-ui"
import { useSuspenseQuery } from "@tanstack/react-query"

import { env } from "../../../app/env"
import { getRepositoryName } from "../../../app/routing"
import {
  useRepository,
  useRepositoryTags,
} from "../../../app/services/repository"
import { useTeamSpace } from "../../../app/services/role"
import { ArchiveDocumentsButton } from "../../BulkActions/ArchiveDocumentsButton"
import { CopyDocumentsToLocale } from "../../BulkActions/CopyDocumentsToLocale"
import { DeleteDocumentsButton } from "../../BulkActions/DeleteDocumentsButton"
import { PublishDocumentsButton } from "../../BulkActions/PublishDocumentsButton"
import type { Tab } from "../../DocumentListHeader/DocumentListHeader"
import { useDocumentListSearchParams } from "../../useDocumentListSearchParams"
import { CreateNewDocumentButton } from "../CreateNewDocumentDialog"
import { FilterBar } from "./FilterBar"
import { FilterDropDown, FilterDropDownBoundary } from "./FilterDropdown"
import { useActionBar } from "./useActionBar"
import { getAuthorDisplayName } from "./utils"

interface ActionBarProps {
  tab: Tab
  setModalOpen: (isOpen: boolean) => void
  createEnabled?: boolean
  releaseId?: string
}

export function ActionBar(props: ActionBarProps) {
  const { tab, setModalOpen, createEnabled = true, releaseId } = props

  const {
    selectedCustomTypeFilters,
    selectedTagFilters,
    selectedAuthorFilters,
    selectedFilters,
    toggleFilter,
    removeFilter,
  } = useActionBar()

  return (
    <Box flexDirection="column" gap={8}>
      <Box
        justifyContent="space-between"
        alignItems="center"
        minHeight={filterBarMinHeight}
      >
        <Box gap={16}>
          <FilterDropDownBoundary
            type="customType"
            selectedFilters={selectedCustomTypeFilters}
          >
            <FilterDropDown
              type="customType"
              optionsFetcher={
                tab !== "upcoming"
                  ? useTeamSpaceCustomTypesFilters
                  : useCustomTypesFilters
              }
              selectedFilters={selectedCustomTypeFilters}
              onFilterSelected={toggleFilter}
            />
          </FilterDropDownBoundary>
          <FilterDropDownBoundary
            type="author"
            selectedFilters={selectedAuthorFilters}
          >
            <FilterDropDown
              type="author"
              optionsFetcher={useRepositoryAuthorsFilters}
              selectedFilters={selectedAuthorFilters}
              onFilterSelected={toggleFilter}
            />
          </FilterDropDownBoundary>
          <FilterDropDownBoundary
            type="tag"
            selectedFilters={selectedTagFilters}
          >
            <FilterDropDown
              type="tag"
              optionsFetcher={useRepositoryTagsFilters}
              selectedFilters={selectedTagFilters}
              onFilterSelected={toggleFilter}
            />
          </FilterDropDownBoundary>
        </Box>

        <Box gap={16} alignItems="center">
          <PublishDocumentsButton releaseId={releaseId} />
          {tab !== "archived" && (
            <ArchiveDocumentsButton releaseId={releaseId} />
          )}
          {tab === "working" && <CopyDocumentsToLocale />}
          {tab === "archived" && <DeleteDocumentsButton />}

          {createEnabled && (
            <CreateNewDocumentButtonBasedOnTab
              tab={tab}
              setModalOpen={setModalOpen}
            />
          )}
        </Box>
      </Box>
      {selectedFilters.length > 0 && (
        <FilterBar
          selectedFilters={selectedFilters}
          onRemoveFilter={removeFilter}
        />
      )}
    </Box>
  )
}

function CreateNewDocumentButtonBasedOnTab(props: ActionBarProps) {
  const { tab, setModalOpen } = props

  if (tab === "working")
    return <CreateNewDocumentButton setModalOpen={setModalOpen} />

  if (tab === "upcoming")
    return (
      <CreateNewDocumentButton
        color="grey"
        size="small"
        setModalOpen={setModalOpen}
      />
    )

  return null
}

export function useCustomTypesFilters() {
  const { data: customTypes } = useSuspenseQuery(
    getCustomTypesOptions({
      authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
      baseUrl: env.PRISMIC_CUSTOM_TYPES_API_BASE_URL,
      repository: getRepositoryName(),
    }),
  )

  return (
    customTypes
      // Filter out custom types that are disabled
      // (status is a boolean field, true means enabled)
      .filter((customType) => customType.status)
      .map((customType) => ({
        label: customType.label,
        id: customType.id,
        key: customType.id,
      }))
  )
}

export function useTeamSpaceCustomTypesFilters() {
  const customTypes = useCustomTypesFilters()

  const { optimisticSearchParams } = useDocumentListSearchParams()
  const teamSpace = useTeamSpace(optimisticSearchParams.space)

  return customTypes.filter(({ id }) => teamSpace?.customTypes?.includes(id))
}

export function useRepositoryTagsFilters() {
  const repositoryCustomTags = useRepositoryTags().results

  return repositoryCustomTags.map((tag) => ({
    label: tag,
    id: tag,
    key: tag,
  }))
}

export function useRepositoryAuthorsFilters() {
  const { authors } = useRepository()

  return authors.map((author) => ({
    label: getAuthorDisplayName(author),
    id: author.id,
    key: author.id,
  }))
}

// The height of the "Create New" Button.
// This is so the bar doesn't jump in size when going from/to screens that have the Button vs not.
const filterBarMinHeight = theme.size.large

export function ActionBarSkeleton() {
  return (
    <Box justifyContent="space-between">
      <Box gap={16}>
        <Skeleton height={24} width={54} />
        <Skeleton height={24} width={74} />
        <Skeleton height={24} width={54} />
      </Box>
      <Skeleton height={24} width={84} />
    </Box>
  )
}
